import type { CritereGrille, GrilleCorrection } from '@silex/shared';

/**
 * LA GRILLE DE CORRECTION, SAISISSABLE — 15 septembre 2026 (Φ12-C).
 *
 * ─── Le mur que ce formulaire lève ────────────────────────────────────────
 *
 * `validerItem` exige une grille dès que `methodeVerification` vaut
 * `grille_modele` ou `grille_humaine`, et `methodeVerification` ne s'édite pas
 * — c'est une décision assumée : la changer déplacerait le verdict d'un moteur
 * à l'autre. Or le champ grille n'existait NULLE PART dans la console. Tout
 * item de cette famille sans grille stockée refusait donc toute correction,
 * sans qu'aucun écran n'offre de quoi combler le manque. `demonstration`, le
 * type le plus nombreux de la file, se corrige par grille.
 *
 * ─── Pourquoi les bornes sont recopiées ici ───────────────────────────────
 *
 * Elles viennent de `grilleSchema` (`corpus.routes.ts`). Le serveur reste le
 * juge ; on les redit ici pour refuser AVANT l'aller-retour, jamais pour s'en
 * écarter. Le message du validateur nomme le CODE DE L'ITEM, pas le champ
 * fautif : illisible pour qui vient de remplir un formulaire.
 */

/** Bornes du schéma serveur — `grilleSchema`, `corpus.routes.ts`. */
export const MAX_CRITERES = 12;
export const MAX_INDICATEURS = 10;
export const POIDS_MAX = 100;

/**
 * Un critère en cours de saisie. `cle` ne part JAMAIS au serveur : elle sert
 * d'identité stable à la ligne, pour que retirer le deuxième critère ne
 * déplace pas le curseur du troisième. La console se tient au clavier.
 */
export interface CritereEdite {
  readonly cle: string;
  readonly code: string;
  readonly libelle: string;
  readonly indicateurs: readonly string[];
  readonly poids: number;
}

export interface EtatGrille {
  readonly seuilReussite: number;
  readonly criteres: readonly CritereEdite[];
}

/** Le seuil d'ouverture d'une grille neuve — la majorité des poids. */
const SEUIL_PAR_DEFAUT = 0.6;

let compteurCles = 0;
const nouvelleCle = () => `critere-${++compteurCles}`;

/** Un critère neuf : tout à saisir, sauf le poids qui part plein. */
export function critereVide(): CritereEdite {
  return { cle: nouvelleCle(), code: '', libelle: '', indicateurs: [], poids: 100 };
}

/**
 * L'état du formulaire d'après ce que l'item porte. Une grille absente ouvre
 * SANS critère : c'est ce qui distingue « rien saisi » de « saisi », et donc
 * ce qui décide si l'on envoie une grille ou non.
 */
export function etatGrilleDepuis(grille: GrilleCorrection | null): EtatGrille {
  if (!grille) return { seuilReussite: SEUIL_PAR_DEFAUT, criteres: [] };
  return {
    seuilReussite: grille.seuilReussite,
    criteres: grille.criteres.map((c) => ({
      cle: nouvelleCle(),
      code: c.code,
      libelle: c.libelle,
      indicateurs: [...c.indicateurs],
      poids: c.poids,
    })),
  };
}

/**
 * Ce qu'on envoie.
 *
 * `version` s'incrémente depuis celle de l'item et n'est jamais saisie à la
 * main : elle entre dans la clé du `VerdictCache`. Changer le barème doit
 * rendre inutilisables les verdicts rendus sous l'ancien — les laisser
 * resservir ferait juger deux copies par deux règles différentes.
 */
export function grilleDepuisEtat(etat: EtatGrille, versionPrecedente: number): GrilleCorrection {
  return {
    version: versionPrecedente + 1,
    seuilReussite: etat.seuilReussite,
    criteres: etat.criteres.map((c): CritereGrille => ({
      code: c.code.trim(),
      libelle: c.libelle.trim(),
      indicateurs: c.indicateurs.map((i) => i.trim()).filter((i) => i.length > 0),
      poids: c.poids,
    })),
  };
}

/**
 * Le refus local, dans l'esprit de `figureIntracable` : ce que le serveur
 * refusera, dit avant l'aller-retour et en désignant le champ. `null` quand
 * il n'y a rien à signaler.
 */
export function grilleRefusee(etat: EtatGrille): string | null {
  const g = grilleDepuisEtat(etat, 0);
  if (g.criteres.length === 0) return 'Une grille porte au moins un critère.';
  if (g.criteres.length > MAX_CRITERES) return `Une grille porte au plus ${MAX_CRITERES} critères.`;
  if (!(etat.seuilReussite > 0) || etat.seuilReussite > 1) {
    return 'Le seuil de réussite se situe entre 0 exclu et 1.';
  }
  if (g.criteres.some((c) => c.code === '' || c.libelle === '')) {
    return 'Chaque critère porte un code et un libellé.';
  }
  if (g.criteres.some((c) => !Number.isFinite(c.poids) || c.poids < 0 || c.poids > POIDS_MAX)) {
    return `Le poids d’un critère se situe entre 0 et ${POIDS_MAX}.`;
  }
  if (g.criteres.reduce((n, c) => n + c.poids, 0) <= 0) {
    return 'Les poids totalisent 0 : aucun critère ne pèserait sur le verdict.';
  }
  return null;
}

/**
 * La grille a-t-elle bougé ?
 *
 * On n'envoie pas un barème identique : l'envoyer ferait avancer `version` et
 * jetterait pour rien les verdicts déjà mémorisés. Les deux côtés passent par
 * la même normalisation, sans quoi une espace en fin de libellé compterait
 * comme un changement de barème.
 */
export function grilleChangee(etat: EtatGrille, origine: GrilleCorrection | null): boolean {
  const saisie = grilleDepuisEtat(etat, 0);
  if (origine === null) return saisie.criteres.length > 0;
  const avant = grilleDepuisEtat(etatGrilleDepuis(origine), 0);
  return JSON.stringify(saisie) !== JSON.stringify(avant);
}

export function ChampsGrille({
  etat,
  onEtat,
  refus,
}: {
  etat: EtatGrille;
  onEtat: (etat: EtatGrille) => void;
  refus: string | null;
}) {
  const majCritere = (i: number, patch: Partial<CritereEdite>) =>
    onEtat({ ...etat, criteres: etat.criteres.map((c, j) => (i === j ? { ...c, ...patch } : c)) });

  const sommePoids = etat.criteres.reduce(
    (n, c) => n + (Number.isFinite(c.poids) ? c.poids : 0),
    0,
  );

  return (
    <div className="card">
      <h3>Grille de correction</h3>

      {refus ? (
        <p className="callout callout--rare" role="status">
          {refus}
        </p>
      ) : null}

      <span className="field field--inline">
        <label htmlFor="g-seuil">Seuil de réussite</label>
        <input
          id="g-seuil"
          className="control"
          type="number"
          min={0.05}
          max={1}
          step={0.05}
          value={etat.seuilReussite}
          onChange={(e) => onEtat({ ...etat, seuilReussite: Number(e.target.value) })}
        />
      </span>

      {etat.criteres.map((c, i) => (
        <div key={c.cle} className="card critere-card">
          <div className="filters">
            <span className="badge">{i + 1}</span>
            <input
              className="control"
              value={c.code}
              aria-label={`Code du critère ${i + 1}`}
              placeholder="Code"
              onChange={(e) => majCritere(i, { code: e.target.value })}
            />
            <input
              className="control"
              value={c.poids}
              type="number"
              min={0}
              max={POIDS_MAX}
              aria-label={`Poids du critère ${i + 1}`}
              onChange={(e) => majCritere(i, { poids: Number(e.target.value) })}
            />
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => onEtat({ ...etat, criteres: etat.criteres.filter((_, j) => i !== j) })}
            >
              Retirer ce critère
            </button>
          </div>

          <span className="field field--bloc">
            <label htmlFor={`g-libelle-${c.cle}`}>Libellé</label>
            <input
              id={`g-libelle-${c.cle}`}
              className="control"
              value={c.libelle}
              onChange={(e) => majCritere(i, { libelle: e.target.value })}
            />
          </span>

          <p className="critere-texte">Ce qu’on cherche dans la copie</p>
          {c.indicateurs.map((indicateur, k) => (
            <div key={k} className="filters">
              <input
                className="control"
                value={indicateur}
                aria-label={`Indicateur ${k + 1} du critère ${i + 1}`}
                onChange={(e) =>
                  majCritere(i, {
                    indicateurs: c.indicateurs.map((x, m) => (k === m ? e.target.value : x)),
                  })
                }
              />
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() =>
                  majCritere(i, { indicateurs: c.indicateurs.filter((_, m) => k !== m) })
                }
              >
                Retirer
              </button>
            </div>
          ))}
          {c.indicateurs.length < MAX_INDICATEURS ? (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => majCritere(i, { indicateurs: [...c.indicateurs, ''] })}
            >
              Ajouter un indicateur
            </button>
          ) : null}
        </div>
      ))}

      <div className="filters">
        {etat.criteres.length < MAX_CRITERES ? (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => onEtat({ ...etat, criteres: [...etat.criteres, critereVide()] })}
          >
            Ajouter un critère
          </button>
        ) : null}
        <span className="note-line">
          {etat.criteres.length} critère{etat.criteres.length > 1 ? 's' : ''} · poids {sommePoids}
        </span>
      </div>
    </div>
  );
}
